/* React */
import { useState, useEffect } from 'react'

/* Redux */
import Redux from './AddToCart.Redux'

/* Material */
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import useStyles from './styles'

/* Types */
import { tProduct } from 'src/shared/frontendTypes/product'
import { imageSrc } from 'src/shared/frontendTypes/images'
import { combination } from '../../functions/getCombination'

/* Components */
import AttributesSelector, { selectedAttributes } from './Components/AttributesSelector'
import QuantitySelector from './Components/QuantitySelector'
import AddToCartButton from './Components/AddToCartButton'
import AddToFavButton from 'src/components/AddToFavButton/AddToFavButton'
import ContactModal from 'src/components/ContactModal/ContactModal'
import PriceDisplay from 'src/components/PriceDisplay'
import DiscountInfos from 'src/components/DiscountInfos'
import StockDiscountInfo from 'src/components/StockDiscountInfo'
import DeliverytimeDisplay from './Components/DeliverytimeDisplay'
import productUrl, { productVariantUrl } from 'src/functions/productUrl'
import { useProductInquiryConfig } from 'src/context/ProductInquiryContext'
import {
  pageBuilderCartAddedButtonSx,
  pageBuilderCartAddedFavoriteButtonSx,
  pageBuilderPurchaseButtonSx,
} from 'src/theme/pageBuilderButtons'
import { isQuantityDiscountAvailable } from 'src/shared/discountShop'
import { getQuantityDiscountCartPreviewText } from 'src/functions/quantityDiscountText'
import type { DisabledAttributeOptions } from '../../functions/attributeAvailability'

export type Status = 'watch' | 'tried' | 'done'
type PendingOptionsAction = 'cart' | 'inquiry'

interface Props {
  product: tProduct
  combination: combination | null
  handleChangeAttribute: (attribute: number, option: number | '') => void
  selectedAttributes: selectedAttributes
  disabledAttributeOptions?: DisabledAttributeOptions
  image?: imageSrc
}

const formatCouponPromotionExpirationDate = (value?: string) => {
  if (!value) return undefined

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return undefined

  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'Europe/Berlin',
    year: 'numeric',
  }).format(date)
}

const AddToCart: React.FC<Props> = ({
  product: { 
    id, categorie, name, brand, brandTitel,
    attributes, path, onlyStock, shippingType, purchaseMode, inquireOnly,
    couponPromotion,
  },
  combination,
  selectedAttributes,
  disabledAttributeOptions = {},
  handleChangeAttribute: changeAttribute,
}) => {
  const classes = useStyles()
  const productInquiry = useProductInquiryConfig()
  const safeAttributes = attributes || []
 // State-Deklarationen
 const [Status, setStatus] = useState<Status>('watch');
 const [Quantity, setQuantity] = useState<number>(1);
 const [useOnlyStock, setUseOnlyStock] = useState(onlyStock);
 const [contactModalOpen, setContactModalOpen] = useState(false);
 const [optionsDialogOpen, setOptionsDialogOpen] = useState(false);
 const [pendingOptionsAction, setPendingOptionsAction] = useState<PendingOptionsAction>('cart');

 // Prüfung und Aktualisierung von useOnlyStock auf Basis der ausgewählten Attribute
 useEffect(() => {
   const isAnySelectedOptionUncultivable = () => {
     return safeAttributes.some(attribute => {
       const selectedOptionId = selectedAttributes[attribute.id];
       const selectedOption = attribute.options.find(option => option.id === selectedOptionId);
       return selectedOption?.uncultivable;
     });
   };
    // Setzen von useOnlyStock basierend auf der uncultivable-Prüfung oder dem initialen onlyStock-Wert
    const SetUncultivable = isAnySelectedOptionUncultivable();
    setUseOnlyStock(SetUncultivable || onlyStock);
 }, [onlyStock, safeAttributes, selectedAttributes]); // Abhängigkeiten zu attributes und selectedAttributes hinzufügen

 // Handler für Attributänderungen
 const handleChangeAttribute = (attribute: number, option: number | '') => {
   changeAttribute(attribute, option);
   if (Status === 'done') setStatus('watch');
 };

 const handleChangeQuantity = (qty: number) => setQuantity(qty);
 const handleChangeStatus = (newStatus: Status) => setStatus(newStatus);
 const allAttributesSelected = safeAttributes.every(
   attribute => typeof selectedAttributes[attribute.id] === 'number'
 );
 const hasMissingAttributes = safeAttributes.length > 0 && !allAttributesSelected
 const resolvedPurchaseMode = purchaseMode || (inquireOnly ? 'inquiry' : 'cart');
  const isInquiryOnly = resolvedPurchaseMode === 'inquiry';
  const showInquiryButton = resolvedPurchaseMode === 'cartInquiry' || isInquiryOnly;
  const inquiryButtonLabel = isInquiryOnly
   ? productInquiry.inquiryOnlyButtonLabel
   : productInquiry.cartInquiryButtonLabel;
  const ctaButtonSx = pageBuilderPurchaseButtonSx
  const cartAdded = Status === 'done'
  const favoriteButtonSx = cartAdded
    ? pageBuilderCartAddedFavoriteButtonSx
    : ctaButtonSx
  const addToCartButtonSx = cartAdded
    ? pageBuilderCartAddedButtonSx
    : ctaButtonSx

  // AttributesSelector ++++++++++++++++++++++++++++++++++++++++++++++++++++++++

 const renderedAttributesSelector = safeAttributes.length > 0
  ? <AttributesSelector
    {...{
      attributes: safeAttributes,
      selectedAttributes,
      handleChangeAttribute,
      Status,
      disabledOptions: disabledAttributeOptions,
    }}
  />
  : null

  const showCouponPromotionBanner = Boolean(
    couponPromotion?.showOnProduct !== false
    && (couponPromotion?.title || couponPromotion?.text || couponPromotion?.badgeText)
  )
  const couponPromotionBannerClass = couponPromotion?.color === 'red'
    ? classes.couponPromotionBannerRed
    : couponPromotion?.color === 'black'
      ? classes.couponPromotionBannerBlack
      : classes.couponPromotionBannerGreen
  const couponPromotionExpirationDate = formatCouponPromotionExpirationDate(couponPromotion?.expirationDate)
  const couponPromotionExpirationLabel = couponPromotionExpirationDate
    ? `bis ${couponPromotionExpirationDate}`
    : undefined
  const couponPromotionBanner = showCouponPromotionBanner ? (
    <div className={[
      classes.couponPromotionBanner,
      couponPromotionBannerClass,
    ].join(' ')}>
      {couponPromotion?.badgeText && (
        <span className={classes.couponPromotionBadge}>
          {couponPromotion.badgeText}
        </span>
      )}
      <div className={classes.couponPromotionContent}>
        {couponPromotion?.title && (
          <strong className={classes.couponPromotionTitle}>
            {couponPromotion.title}
          </strong>
        )}
        {couponPromotion?.text && (
          <span className={classes.couponPromotionText}>
            {couponPromotion.text}
          </span>
        )}
        {couponPromotionExpirationLabel && (
          <span className={classes.couponPromotionExpires}>
            {couponPromotionExpirationLabel}
          </span>
        )}
      </div>
    </div>
  ) : null

  // In den Warenkorb ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

  const { getItem, addItem } = Redux()

  const handleAddToCart = () => {

    if (!combination) return
    const {
      key, price, priceLock, images, deliverytime, stockDeliverytime,
      stock, hash,
    } = combination
    if (useOnlyStock && (stock || 0) <= 0) {
      handleChangeStatus('tried')
      return
    }

    const cartItem = getItem(key)
    const cartQty = cartItem ? cartItem.qty : 0    

    const allesAusgewahlt = (attributes:selectedAttributes): attributes is Record<string, number> => {
      return Object.values(attributes).every(a => typeof a === 'number')
    }

    if (hasMissingAttributes){
      handleChangeStatus('tried')
      setPendingOptionsAction('cart')
      setOptionsDialogOpen(true)
      return
    }

    const cartAttributes = allesAusgewahlt(selectedAttributes)
      ? selectedAttributes
      : {}


    let setQty = Quantity

    if ( useOnlyStock && ((Quantity+cartQty) > (stock || 0)) ) {
      if (cartQty < (stock || 0)) setQty = (stock || 0) - cartQty
      else {
        handleChangeStatus('tried')
        return
      }
    }

    const attributesText = safeAttributes.map(attribute => ({
      titel: attribute.name,
      option:
        attribute.options.find(o => o.id === selectedAttributes[attribute.id])
          ?.name || '',
    }))

    addItem({
      id,
      categorie,
      key,
      path,
      name,
      brand: {
        id: brand,
        titel: brandTitel,
      },
      price: price,
      priceLock,
      qty: setQty,
      attributes: cartAttributes,
      attributesText,
      image: images[0],
      deliverytime,
      stockDeliverytime,
      shippingType,
      purchaseMode: resolvedPurchaseMode,
      stock, 
      onlyStock: useOnlyStock,
      hash,
    })
    setOptionsDialogOpen(false)
    handleChangeStatus('done')
  }

  const closeOptionsDialog = () => {
    setOptionsDialogOpen(false)
    setPendingOptionsAction('cart')
  }

  const handleInquiryClick = () => {
    if (hasMissingAttributes) {
      handleChangeStatus('tried')
      setPendingOptionsAction('inquiry')
      setOptionsDialogOpen(true)
      return
    }

    setOptionsDialogOpen(false)
    setPendingOptionsAction('cart')
    setContactModalOpen(true)
  }

  const productPageUrl = productUrl(id, path)
  const productPageLink = typeof window === 'undefined'
    ? productPageUrl
    : new URL(productPageUrl, window.location.origin).toString()
  const buildContactMessage = (variant: string, link: string) => [
    'Guten Tag,',
    '',
    'ich interessiere mich für folgenden Artikel:',
    `Artikel: ${name} (#${id})`,
    `Variante: ${variant}`,
    `Link: ${link}`,
    '',
    'Bitte senden Sie mir weitere Informationen bzw. ein Angebot.',
  ].join('\n')
  const fallbackContactMessage = buildContactMessage('Anfrage / individuelle Konfiguration', productPageLink)

  const optionsDialog = safeAttributes.length > 0
    ? <Dialog
      open={optionsDialogOpen}
      onClose={closeOptionsDialog}
      fullWidth
      maxWidth='sm'
    >
      <DialogTitle>Optionen auswählen</DialogTitle>
      <DialogContent className={classes.optionsDialogContent}>
        <Typography variant='body1' className={classes.optionsDialogText}>
          Es sind noch nicht alle Optionen ausgewählt. Bitte wählen Sie die fehlenden Optionen aus.
        </Typography>
        <AttributesSelector
          attributes={safeAttributes}
          selectedAttributes={selectedAttributes}
          handleChangeAttribute={handleChangeAttribute}
          Status='tried'
          disabledOptions={disabledAttributeOptions}
        />
      </DialogContent>
      <DialogActions className={classes.optionsDialogActions}>
        <Button onClick={closeOptionsDialog}>
          Abbrechen
        </Button>
        <Button
          variant='contained'
          color='primary'
          sx={ctaButtonSx}
          disabled={hasMissingAttributes}
          onClick={pendingOptionsAction === 'inquiry' ? handleInquiryClick : handleAddToCart}
        >
          {pendingOptionsAction === 'inquiry' ? inquiryButtonLabel : 'In den Warenkorb'}
        </Button>
      </DialogActions>
    </Dialog>
    : null

  // Keine Kombination +++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  if (!combination) return <div className={classes.container}>
    {renderedAttributesSelector}

    <Alert severity="info">
      Dieser Artikel ist aktuell nicht direkt online bestellbar. Kontaktieren Sie uns für Beratung, Verfügbarkeit und ein individuelles Angebot.
    </Alert>

    <div className={classes.secondaryActionRow}>
      <Button
        variant='contained'
        color='primary'
        size='large'
        fullWidth
        className={classes.inquiryButton}
        sx={ctaButtonSx}
        onClick={() => setContactModalOpen(true)}
      >
        {productInquiry.inquiryOnlyButtonLabel}
      </Button>
    </div>

    <ContactModal
      open={contactModalOpen}
      initialSubject='angebot'
      initialMessage={fallbackContactMessage}
      onClose={() => setContactModalOpen(false)}
    />
  </div>

  // Render ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

  const { key, price, deliverytime, stockDeliverytime, stock } = combination
  const onlyStockUnavailable = Boolean(useOnlyStock && (stock || 0) <= 0)
  const cartItem = getItem(key)
  const cartQuantity = Math.max(0, Math.floor(cartItem?.qty || 0))
  const quantityDiscountPreviewQty = Quantity + cartQuantity
  const showQuantityDiscount = typeof price !== 'number'
    && price.showPercent !== false
    && isQuantityDiscountAvailable(price.quantityDiscount, {
      onlyStock: useOnlyStock,
      stock,
    })
  const quantityDiscountText = typeof price !== 'number' && showQuantityDiscount
    ? getQuantityDiscountCartPreviewText(price.quantityDiscount, cartQuantity, Quantity)
    : undefined
  const articleUrl = productVariantUrl(id, path, combination.key)
  const articleLink = typeof window === 'undefined'
    ? articleUrl
    : new URL(articleUrl, window.location.origin).toString()
  const combinationAttributes = combination.attributes || []
  const articleVariant = combinationAttributes.length > 0
    ? combinationAttributes.join(', ')
    : 'Standard'
  const contactMessage = buildContactMessage(articleVariant, articleLink)

  if (isInquiryOnly) return <div className={classes.container}>
    {renderedAttributesSelector}

    {typeof price !== 'number' && (
      <DiscountInfos
        discount={price}
        qty={quantityDiscountPreviewQty}
        component='div'
        variant='h5'
        align='center'
        className={classes.discountInfos}
        clock={{ variant: 'h5' }}
        size='medium'
        showQuantityDiscount={showQuantityDiscount}
        quantityDiscountText={quantityDiscountText}
      />
    )}

    <PriceDisplay
      {...{ price }}
      component='div'
      variant='h4'
      align='left'
      layout='detail'
      showTaxHint
      className={classes.priceDisplay}
      onlyStock={useOnlyStock}
      stock={stock}
    />

    <StockDiscountInfo
      price={price}
      qty={Quantity}
      className={classes.stockDiscountInfo}
    />

    {couponPromotionBanner}

    <DeliverytimeDisplay
      {...{ deliverytime, onlyStock: useOnlyStock }}
      stockDeliverytime={stockDeliverytime}
      inquireOnly
      inquiredQty={Quantity}
      stockQty={stock}
    />

    <Alert severity='info'>
      {productInquiry.onlyInquiryNotice}
    </Alert>

    {safeAttributes.length > 0 && !allAttributesSelected && (
      <Alert severity='warning'>
        {productInquiry.selectionHint}
      </Alert>
    )}

    <div className={classes.buttonsWrapper}>
      <AddToFavButton
        pId={id}
        pKey={key}
        className={classes.addToFavButton}
        sx={ctaButtonSx}
        color='primary' size='large' variant='contained' fullWidth
      />
      <Button
        variant='contained'
        color='primary'
        size='large'
        fullWidth
        className={classes.inquiryButton}
        sx={ctaButtonSx}
        onClick={handleInquiryClick}
      >
        {inquiryButtonLabel}
      </Button>
    </div>

    <ContactModal
      open={contactModalOpen}
      initialSubject='angebot'
      initialMessage={contactMessage}
      onClose={() => setContactModalOpen(false)}
    />

    {optionsDialog}
  </div>

  return <div className={classes.container}>
    {renderedAttributesSelector}

    {!onlyStockUnavailable &&
      <QuantitySelector
        productId={id}
        {...{ Quantity, handleChangeQuantity, onlyStock:useOnlyStock, stock }}
      />
    }

    {typeof price !== 'number' && (
      <DiscountInfos
        discount={price}
        qty={quantityDiscountPreviewQty}
        component='div'
        variant='h5'
        align='center'
        className={classes.discountInfos}
        clock={{ variant: 'h5' }}
        size='medium'
        showQuantityDiscount={showQuantityDiscount}
        quantityDiscountText={quantityDiscountText}
      />
    )}

    <PriceDisplay
      {...{ price }}
      component='div'
      variant='h4'
      align='left'
      layout='detail'
      showTaxHint
      className={classes.priceDisplay}
      onlyStock={useOnlyStock}
      stock={stock}
    />

    <StockDiscountInfo
      price={price}
      qty={Quantity}
      className={classes.stockDiscountInfo}
    />

    {couponPromotionBanner}
    
    <DeliverytimeDisplay
      {...{ deliverytime, onlyStock:useOnlyStock }}
      stockDeliverytime={stockDeliverytime}
      inquireOnly={isInquiryOnly}
      inquiredQty={Quantity}
      stockQty={stock}
    />

    <div className={classes.buttonsWrapper}>
      <AddToFavButton 
        pId={id}
        pKey={key}
        className={classes.addToFavButton}
        sx={favoriteButtonSx}
        color='primary' size='large' variant='contained' fullWidth
      />
      <AddToCartButton
        {...{ handleAddToCart, Status }}
        disabled={onlyStockUnavailable}
        label={onlyStockUnavailable ? 'Nicht verfügbar' : quantityDiscountText?.actionLabel}
        sx={addToCartButtonSx}
      />
    </div>

    {showInquiryButton && !onlyStockUnavailable && (
      <div className={classes.secondaryActionRow}>
        <Button
          variant='contained'
          color='primary'
          size='large'
          fullWidth
          className={classes.inquiryButton}
          sx={ctaButtonSx}
          onClick={handleInquiryClick}
        >
          {inquiryButtonLabel}
        </Button>
      </div>
    )}

    {cartItem &&
      <div className={classes.InCartInfo}>
        Artikel {cartItem.qty} x im Warenkorb
      </div>
    }

    {showInquiryButton && !onlyStockUnavailable && (
      <ContactModal
        open={contactModalOpen}
        initialSubject='angebot'
        initialMessage={contactMessage}
        onClose={() => setContactModalOpen(false)}
      />
    )}

    {optionsDialog}

  </div>

  // +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
}

export default AddToCart
